import { notFound } from '@veraclins-dev/react-utils/server';
import { data } from 'react-router';

import { CaseStudy } from '../components/work/case-study';
import { getCaseStudy } from '../content/case-studies';
import {
  absoluteUrl,
  defaultSocialImage,
  siteBaseUrl,
} from '../content/metadata';

import { type Route } from './+types/work.$slug';

export async function loader({ params }: Route.LoaderArgs) {
  const slug = params.slug;

  if (!slug) {
    throw notFound('work', { message: 'Case study not found' });
  }

  const caseStudy = getCaseStudy(slug);

  if (!caseStudy) {
    throw notFound('work', { message: 'Case study not found' });
  }

  return data({ caseStudy, slug });
}

export const meta: Route.MetaFunction = ({ loaderData }) => {
  const caseStudy = loaderData?.caseStudy;
  const slug = loaderData?.slug;

  const title = caseStudy
    ? `${caseStudy.title} - Work | Clinton Agada`
    : 'Work | Clinton Agada';

  const description =
    caseStudy?.tagline ||
    'A case study by Clinton Agada showing the problem, what he did, and how it turned out.';

  const url = slug ? absoluteUrl(`/work/${slug}`) : absoluteUrl('/work');

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: defaultSocialImage },
    { property: 'og:url', content: url },
    { property: 'og:site_name', content: 'clintonagada.dev' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: defaultSocialImage },
    { tagName: 'link', rel: 'canonical', href: url },
  ];
};

export default function CaseStudyPage({ loaderData }: Route.ComponentProps) {
  const { caseStudy } = loaderData;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: caseStudy.title,
    description: caseStudy.tagline,
    creator: {
      '@type': 'Person',
      name: 'Clinton Agada',
      url: siteBaseUrl,
      jobTitle: 'Senior Software Engineer',
    },
    keywords: caseStudy.stack.join(', '),
    about: caseStudy.kind,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <CaseStudy caseStudy={caseStudy} />
    </>
  );
}
